import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { CommonIcons } from 'components/Icons';
import { SIZE_ICON } from 'consts/data';
import { Button as ButtonShadcn } from 'shadcn/components/button';
import { cn } from 'helpers/css';
import { get } from 'lodash';

const MIN_HEIGHT_BTN = 32;

/**
 *
 * @param {Object} props
 * @param {Object} props.className
 * @param {Object} props.style
 * @param {boolean} props.isUpload
 * @param {boolean} props.isLoading
 * @param {boolean} props.isPulsing
 * @param {boolean} props.disabled
 * @param {(event, { ref: refUpload, setUploading }) => void} props.onChangeFile
 * @param {string} props.acceptFile
 * @param {"default" | "destructive" | "outline" | "secondary" | "ghost" | "link"} props.variant
 * @param {boolean} props.triggerByEnter
 * @returns
 */
const Button = ({
  children,
  className,
  style,
  isUpload,
  isLoading,
  isPulsing,
  disabled,
  onChangeFile,
  acceptFile,
  triggerByEnter,
  variant,
  color,
  outline,
  ...props
}) => {
  //! State
  const btnRef = useRef(null);
  const refUpload = useRef(null);
  const [isUploading, setUploading] = useState(false);

  const sharedProps = useMemo(() => {
    return {
      'tracking-el': '1',
    };
  }, []);

  //! Function
  useEffect(() => {
    //* Styling when button have icon
    setTimeout(() => {
      if (btnRef.current) {
        const svgDOM = btnRef.current.querySelector('svg');
        if (svgDOM) {
          const content = btnRef.current.querySelector('.content');
          const isOnlySVG = (content?.childNodes || []).length <= 1;

          content.style.display = 'flex';
          content.style.alignItems = 'center';
          content.style.justifyContent = 'center';
          if (!isOnlySVG) {
            svgDOM.style.marginRight = '8px';
          }
        }
      }
    }, 0);
  }, [btnRef]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Enter') {
        btnRef && btnRef.current && btnRef.current.click();
      }
    };

    if (triggerByEnter) {
      window.addEventListener('keydown', handler);
    }

    return () => {
      if (triggerByEnter) {
        window.removeEventListener('keydown', handler);
      }
    };
  }, [triggerByEnter]);

  const variantTransform = useMemo(() => {
    //* Migrate from bootstrap -> shadcn
    const mappingVariants = {
      text: 'ghost',
      danger: 'destructive',
    };

    return outline
      ? 'outline'
      : get(mappingVariants, variant) || get(mappingVariants, color) || variant;
  }, [variant, color]);

  //! Render
  if (isUpload) {
    return (
      <Fragment>
        <input
          type='file'
          style={{ display: 'none' }}
          ref={refUpload}
          onChange={(e) => onChangeFile(e, { ref: refUpload, setUploading })}
          accept={acceptFile}
        />
        <ButtonShadcn
          innerRef={btnRef}
          className={classNames('new-design-btn', className)}
          isLoading={isUploading}
          style={{ ...(style || {}), minHeight: MIN_HEIGHT_BTN }}
          {...props}
          onClick={() => {
            if (refUpload.current) {
              refUpload.current.click();
            }
          }}
        >
          <CommonIcons.Upload size={SIZE_ICON} /> {children}
        </ButtonShadcn>
      </Fragment>
    );
  }

  return (
    <ButtonShadcn
      className={cn({ pulsing: isPulsing }, className)}
      style={{ minHeight: MIN_HEIGHT_BTN, ...(style || {}) }}
      isLoading={isLoading}
      disabled={disabled}
      variant={variantTransform}
      {...sharedProps}
      {...props}
    >
      {children}
    </ButtonShadcn>
  );
};

export default React.memo(Button);
